'use client';

import { useCallback, useState } from 'react';
import clsx from 'clsx';
import type { ButtonVariantProps, ButtonEventHandler } from '../types';
import AddCircle from '@pixum/svg-icon/lib/Standard/AddCircle';
import ButtonComponent, { ButtonVariants, BUTTON_STATES } from '@pixum/button';
import { useAddToCart, useProductInCart } from 'hooks';
import { CheckMarkRound } from '@pixum/svg-icon';
import { LINK_TYPES } from 'data-layer/constants';
import { getCurrentHostAndPath, isLinkType, parseStringToNumber, trackEvent } from 'utils';
import '../styles.css';

const TRACKING_CONTEXT = 'AddToCart';

/**
 * Renders a Button component with add to cart functionality.
 * Uses the useAddToCart hook to handle adding products to the cart with loading state management.
 * Uses the useProductInCart hook to check if the product is already in the cart on mount.
 *
 * @param props - ButtonVariantProps
 * @returns A Button component with add to cart functionality or null if no link/productId is provided.
 */
export function ButtonWithAddToCart(props: ButtonVariantProps) {
  const { className, link, variant = ButtonVariants.TERTIARY, device, size, id, onClick, constants } = props;
  const productId = link?.productId;
  const numericProductId = parseStringToNumber(productId);

  const { addToCart, isLoading } = useAddToCart();
  const { isInCart } = useProductInCart(productId);
  const [ctaStatus, setCtaStatus] = useState(false);
  const successLabel = (constants?.['i18n_added'] as string) ?? '';

  // Show success state if product is already in cart or was just added
  const showSuccessState = ctaStatus || isInCart;

  const handleAddToCart = useCallback(
    async (event: React.MouseEvent<HTMLAnchorElement | HTMLButtonElement>) => {
      if (!isLinkType(link, LINK_TYPES.ADD_TO_CART)) {
        return;
      }

      event.preventDefault();
      event.stopPropagation();

      if (!numericProductId) {
        console.error('Cannot add product to cart: productId is missing');

        return;
      }

      try {
        await addToCart(numericProductId);

        setCtaStatus(true);

        trackEvent(TRACKING_CONTEXT, numericProductId, getCurrentHostAndPath());
      } catch {
        // Error is already logged by the hook
        console.log('Can not add to cart');
      }

      // Call the optional onClick handler if provided
      if (onClick) {
        onClick(event);
      }
    },
    [link, addToCart, onClick, numericProductId]
  );

  if (!link || !isLinkType(link, LINK_TYPES.ADD_TO_CART)) {
    return null;
  }

  const componentClass = clsx(className, `button-for-${device}`);

  // Helper function to determine button state
  const getButtonState = () => {
    if (isLoading) return BUTTON_STATES.BUSY;
    if (showSuccessState) return BUTTON_STATES.SUCCESS;

    return BUTTON_STATES.DEFAULT;
  };

  const title = showSuccessState ? successLabel : (link?.title as string);

  return (
    <ButtonComponent
      className={componentClass}
      label={link?.mouseOverText || title}
      title={title}
      type="button"
      variant={variant}
      size={size}
      id={id}
      onClick={handleAddToCart as ButtonEventHandler}
      state={getButtonState()}
      isClickable={!showSuccessState}
      leadingIcon={showSuccessState ? <CheckMarkRound /> : <AddCircle />}
    >
      {title}
    </ButtonComponent>
  );
}
